import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ClipboardIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { PencilIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import RepresentativeEditModal from "./RepresentativeEditModal";
import Button from "../../components/controls/Button";
import Modal from "../../components/controls/Modal";
import DataGrid from "../../components/Datagrid";
import FormInput from "../../components/controls/FormInput";

export default function AdminRepresentativesTable() {
  const { t } = useTranslation();
  const [representatives, setRepresentatives] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [repToEdit, setRepToEdit] = useState(null);

  useEffect(() => {
    loadRepresentatives();
  }, []);

  const loadRepresentatives = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/all`
      );
      if (!response.ok) throw new Error("Erreur chargement représentants");
      const reps = await response.json();
      setRepresentatives(Array.isArray(reps) ? reps : []);
    } catch {
      setError("Erreur lors du chargement des représentants.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rep) => {
    setRepToEdit(rep);
    setEditModalOpen(true);
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 1500);
  };

  const filteredReps = useMemo(() => {
    const q = String(search || "")
      .toLowerCase()
      .trim();
    if (!q) return representatives || [];
    return (representatives || []).filter((r) => {
      const firstName = String(r.firstName || "").toLowerCase();
      const lastName = String(r.lastName || "").toLowerCase();
      const code = String(r.code || "").toLowerCase();
      return firstName.includes(q) || lastName.includes(q) || code.includes(q);
    });
  }, [representatives, search]);

  const rows = useMemo(() => {
    return (filteredReps || []).map((rep) => ({
      id: rep.id,
      lastName: rep.lastName || "—",
      firstName: rep.firstName || "—",
      code: rep.code || "—",
      publicProfile: rep,
      isActive: !!rep.isActive,
      parentName: rep.parentName || "—",
      raw: rep,
    }));
  }, [filteredReps]);

  const columns = useMemo(
    () => [
      {
        key: "lastName",
        label: "Nom",
        width: 220,
        render: (row) => <div className="px-4 py-4">{row.lastName}</div>,
      },
      {
        key: "firstName",
        label: "Prénom",
        width: 220,
        render: (row) => <div className="px-4 py-4">{row.firstName}</div>,
      },
      {
        key: "code",
        label: "Code",
        width: 220,
        render: (row) => <div className="px-4 py-4 font-mono">{row.code}</div>,
      },
      {
        key: "publicProfile",
        label: "Profil public",
        width: 360,
        render: (row) => {
          const rep = row.publicProfile || {};
          return (
            <div className="px-4 py-4 text-sm text-gray-700">
              {rep.city || ""}
              {rep.city ? <br /> : null}
              {rep.publicCity || ""}
              {rep.publicCity ? <br /> : null}
              {rep.phone_number || ""}
              {rep.phone_number ? <br /> : null}
              {rep.email || ""}
            </div>
          );
        },
      },
      {
        key: "isActive",
        label: "Affiché",
        width: 140,
        render: (row) => (
          <div className="px-4 py-4">
            {row.isActive ? (
              <CheckIcon className="w-5 h-5 text-green-500" />
            ) : (
              <span className="text-red-500">Non</span>
            )}
          </div>
        ),
      },
      {
        key: "parentName",
        label: "Parrain",
        width: 220,
        render: (row) => <div className="px-4 py-4">{row.parentName}</div>,
      },
      {
        key: "actions",
        label: "Actions",
        width: 160,
        render: (row) => (
          <div className="px-3 py-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleCopy(row.code)}
              className="btn btn-sm btn-ghost"
              title="Copier le code"
            >
              <AnimatePresence mode="wait" initial={false}>
                {copiedCode === row.code ? (
                  <motion.span
                    key="check"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CheckIcon className="w-5 h-5 text-green-500" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="clipboard"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 2 }}
                    transition={{ duration: 0.4 }}
                  >
                    <ClipboardIcon className="w-5 h-5 text-gray-700" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            <button
              type="button"
              onClick={() => handleEdit(row.raw)}
              className="btn btn-sm btn-ghost"
              title="Consulter / modifier le représentant"
            >
              <PencilIcon className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        ),
      },
    ],
    [copiedCode]
  );

  const renderContent = () => {
    if (error) return <div className="text-center text-red-500">{error}</div>;

    return (
      <DataGrid
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="Aucun représentant trouvé."
      />
    );
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <Button
        type="button"
        color="primary"
        size="sm"
        onClick={() => navigate(-1)}
        leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
      >
        {t("global.back")}
      </Button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">{t("Liste des représentants")}</h1>
        <div className="w-full md:w-72">
          <FormInput
            label=""
            value={search}
            placeholder="Rechercher par nom, prénom ou code..."
            onChange={(name, value) => setSearch(value)}
          />
        </div>
      </div>

      {renderContent()}

      <Modal
        isOpen={editModalOpen && !!repToEdit}
        onClose={() => setEditModalOpen(false)}
        title={t("Liste des représentants")}
        size="xl"
        showCloseButton
      >
        <div className="h-[80vh] overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto pr-1">
            {editModalOpen && repToEdit && (
              <RepresentativeEditModal
                rep={repToEdit}
                onClose={() => setEditModalOpen(false)}
                onSave={loadRepresentatives}
                allRepresentatives={representatives}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
